/**
 * OwnerAdminClientErrors — surface unhandled production JS errors before
 * customers complain.
 *
 * Listing comes from `GET /api/owner-admin/client-errors` (admin-only).
 * The frontend reporter (`lib/clientErrorReporter.js`) and the Error
 * Boundary both post to `/api/client-errors`. Server dedupes by
 * fingerprint + 5-min window and bumps a count.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import { toast } from 'sonner';
import {
  AlertOctagon,
  RefreshCw,
  CheckCircle2,
  RotateCcw,
  Code2,
  Globe,
  Building2,
  Mail,
  Clock,
  Wand2,
  Tag,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtRelative = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

const KIND_BADGE = {
  error: { label: 'JS Error', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  unhandledrejection: { label: 'Async', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  react_error_boundary: { label: 'Render', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
};

export default function OwnerAdminClientErrors() {
  const [tab, setTab] = useState('unresolved');
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const resolved = tab === 'resolved' ? 'true' : tab === 'all' ? '' : 'false';
      const params = new URLSearchParams({ limit: '200' });
      if (resolved !== '') params.set('resolved', resolved);
      if (activeCategory) params.set('category', activeCategory);
      const res = await fetch(`${API_URL}/api/owner-admin/client-errors?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('fetch failed');
      const data = await res.json();
      setRows(Array.isArray(data.errors) ? data.errors : []);
      setCategories(Array.isArray(data.categories) ? data.categories : []);
      setUnresolvedCount(data.unresolved_count || 0);
    } catch (e) {
      console.error(e);
      toast.error('Could not load client errors');
    } finally {
      setLoading(false);
    }
  }, [tab, activeCategory]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const recategorize = async () => {
    setBulkBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/client-errors/recategorize`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Re-tagged ${data.updated} record${data.updated === 1 ? '' : 's'}`);
      fetchRows();
    } catch { toast.error('Could not re-categorize'); }
    finally { setBulkBusy(false); }
  };

  const bulkResolve = async (categoryKey, label, count) => {
    if (!window.confirm(`Mark all ${count} unresolved errors in "${label}" as resolved? This cannot be undone in one click.`)) return;
    setBulkBusy(true);
    try {
      const res = await fetch(
        `${API_URL}/api/owner-admin/client-errors/bulk-resolve?category=${encodeURIComponent(categoryKey)}`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Resolved ${data.resolved_count} errors`);
      fetchRows();
    } catch { toast.error('Bulk resolve failed'); }
    finally { setBulkBusy(false); }
  };

  const markResolved = async (errorId) => {
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/client-errors/${errorId}/resolve`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error();
      toast.success('Marked resolved');
      setSelected(null);
      fetchRows();
    } catch { toast.error('Could not mark resolved'); }
  };

  const markUnresolved = async (errorId) => {
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/client-errors/${errorId}/unresolve`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error();
      toast.success('Reopened');
      setSelected(null);
      fetchRows();
    } catch { toast.error('Could not reopen'); }
  };

  return (
    <div className="space-y-6" data-testid="owner-admin-client-errors">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <AlertOctagon className="w-6 h-6 text-rose-400" />
            Client Errors
            {unresolvedCount > 0 && (
              <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30" data-testid="cerr-unresolved-count">
                {unresolvedCount} unresolved
              </Badge>
            )}
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Unhandled JS errors auto-reported from production browsers. Deduped by fingerprint over a 5-min window.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchRows}
          disabled={loading}
          data-testid="cerr-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setActiveCategory(null); }}>
        <TabsList>
          <TabsTrigger value="unresolved" data-testid="cerr-tab-unresolved">Unresolved</TabsTrigger>
          <TabsTrigger value="resolved" data-testid="cerr-tab-resolved">Resolved</TabsTrigger>
          <TabsTrigger value="all" data-testid="cerr-tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Auto-categorized buckets — one-click bulk resolve */}
      {tab === 'unresolved' && categories.length > 0 && (
        <Card data-testid="cerr-categories-panel">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-1.5">
                <Wand2 className="w-4 h-4 text-emerald-400" />
                Auto-classified buckets
              </CardTitle>
              <p className="text-xs text-zinc-500 mt-1">
                Common, known-cause errors. Click "Resolve all" once the fix is deployed.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={recategorize}
              disabled={bulkBusy}
              data-testid="cerr-recategorize-btn"
              className="text-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${bulkBusy ? 'animate-spin' : ''}`} />
              Re-tag
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {categories.map(cat => {
              const isActive = activeCategory === cat.key;
              return (
                <div
                  key={cat.key}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${
                    isActive ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                  data-testid={`cerr-category-${cat.key}`}
                >
                  <button
                    onClick={() => setActiveCategory(isActive ? null : cat.key)}
                    className="flex-1 text-left min-w-0"
                    data-testid={`cerr-category-filter-${cat.key}`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <Tag className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                      <p className="text-sm font-medium text-zinc-100 truncate">{cat.label}</p>
                      <Badge variant="outline" className="text-[10px] bg-zinc-700/40 text-zinc-300 border-zinc-600">
                        {cat.count}
                      </Badge>
                    </div>
                    <p className="text-xs text-zinc-500">{cat.description}</p>
                  </button>
                  <Button
                    size="sm"
                    onClick={() => bulkResolve(cat.key, cat.label, cat.count)}
                    disabled={bulkBusy}
                    className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
                    data-testid={`cerr-bulk-resolve-${cat.key}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    Resolve all
                  </Button>
                </div>
              );
            })}
            {activeCategory && (
              <button
                onClick={() => setActiveCategory(null)}
                className="text-xs text-zinc-500 hover:text-zinc-300 mt-1"
                data-testid="cerr-clear-category-filter"
              >
                ← Clear filter
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">
            {rows.length} record{rows.length === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-zinc-500" data-testid="cerr-empty">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
              <p className="text-sm">
                {tab === 'resolved' ? 'No resolved errors yet.' : 'Quiet on the western front — no errors reported.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {rows.map(err => {
                const kindMeta = KIND_BADGE[err.kind] || KIND_BADGE.error;
                return (
                  <button
                    key={err.error_id}
                    onClick={() => setSelected(err)}
                    className="w-full text-left px-5 py-3 hover:bg-white/[0.02] transition-colors flex items-start gap-4"
                    data-testid={`cerr-row-${err.error_id}`}
                  >
                    <div className="flex-shrink-0 pt-1">
                      {err.resolved
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        : <AlertOctagon className="w-4 h-4 text-rose-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] ${kindMeta.cls}`}>{kindMeta.label}</Badge>
                        {err.category && (
                          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
                            <Tag className="w-2.5 h-2.5 mr-0.5" />
                            {err.category.replace(/_/g, ' ')}
                          </Badge>
                        )}
                        {err.count > 1 && (
                          <Badge variant="outline" className="text-[10px] bg-zinc-700/40 text-zinc-300 border-zinc-600">
                            ×{err.count}
                          </Badge>
                        )}
                        <span className="text-xs text-zinc-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {fmtRelative(err.last_seen_at)}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-zinc-100 truncate">
                        {err.message || '(no message)'}
                      </p>
                      {err.first_stack_frame && (
                        <p className="text-xs text-zinc-500 font-mono truncate mt-0.5">
                          {err.first_stack_frame}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-zinc-500 flex-wrap">
                        {err.url && (
                          <span className="flex items-center gap-1 truncate max-w-[280px]">
                            <Globe className="w-3 h-3" /> {err.url.replace(/^https?:\/\//, '')}
                          </span>
                        )}
                        {err.workspace_id && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {err.workspace_id}
                          </span>
                        )}
                        {err.user_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {err.user_email}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="w-5 h-5 text-rose-400" />
              Error Detail
            </DialogTitle>
            <DialogDescription>Full stack trace and request context for this error.</DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4 text-sm" data-testid="cerr-detail">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Message</p>
                <p className="font-mono text-zinc-100 break-words">{selected.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">First seen</p>
                  <p className="text-zinc-300">{selected.first_seen_at}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Last seen</p>
                  <p className="text-zinc-300">{selected.last_seen_at}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Count</p>
                  <p className="text-zinc-300">{selected.count}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Kind</p>
                  <p className="text-zinc-300">{selected.kind}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">URL</p>
                  <p className="text-zinc-300 break-all">{selected.url}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Workspace</p>
                  <p className="text-zinc-300">{selected.workspace_id || '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">User</p>
                  <p className="text-zinc-300">{selected.user_email || selected.user_id || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">User Agent</p>
                  <p className="text-zinc-400 text-xs break-words">{selected.user_agent}</p>
                </div>
              </div>

              {selected.stack && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Stack Trace</p>
                  <pre className="text-xs bg-zinc-950 border border-white/10 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono text-zinc-300 max-h-[300px] overflow-y-auto">
                    {selected.stack}
                  </pre>
                </div>
              )}

              {selected.resolved && (
                <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-md px-3 py-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Resolved {selected.resolved_at ? `at ${selected.resolved_at}` : ''}
                  {selected.resolved_by ? ` by ${selected.resolved_by}` : ''}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                {selected.resolved ? (
                  <Button
                    variant="outline"
                    onClick={() => markUnresolved(selected.error_id)}
                    data-testid="cerr-unresolve-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Reopen
                  </Button>
                ) : (
                  <Button
                    onClick={() => markResolved(selected.error_id)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="cerr-resolve-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark Resolved
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
